// Third Party
import React from 'react';
import classNames from 'classnames';
import { camelCase } from 'lodash';

// Proposal
import * as ProposalNS from '../../../Proposal';
import { runBuildRules, validateDisableRules } from '../../../ProposalHelpers';

// Utils
import { formatLocalizedString } from '@/utils/i18n/i18n';

import User from '@/types/user';
import LocalizedString from '@/types/localized-string';

// Components
import HideableLayout from './HideableLayout';
import ToggleSwitch from '@/views/components/inputs/ToggleSwitch';
import InfoTooltip from '@/views/components/tooltips/InfoTooltip';
import { LoadingSpinner } from '@/views/components/common';
import Radio from '@/views/components/inputs/Radio';
import { Checkbox } from '@/views/components/inputs';

type ProposalSectionProps = {
  section: ProposalNS.ProposalTemplateSectionElement;
  showWholeDivision: boolean;
  divisionDisabled?: boolean;
  proposal: ProposalNS.Proposal;
  user: User;
  selectedSections: string[];
  filterQuery: string;
  overrideTooltipPosition?: (coords: { left: number; top: number }) => { left: number; top: number };
  results: Array<LocalizedString>;
  onToggleSection: (section: string, selected: boolean) => void;
  selectedSectionsLocalState: Map<string, boolean>;
  setSelectedSectionsLocalState: React.Dispatch<React.SetStateAction<Map<string, boolean>>>;
  showLoaders: boolean;
  onToggleHiddenLayout: (layout: string, selected: boolean) => void;
  hiddenLayouts: string[];
};

const ProposalSection = ({
  section,
  showWholeDivision,
  divisionDisabled,
  proposal,
  user,
  selectedSections,
  filterQuery,
  overrideTooltipPosition,
  results,
  onToggleSection,
  selectedSectionsLocalState,
  setSelectedSectionsLocalState,
  showLoaders,
  onToggleHiddenLayout,
  hiddenLayouts,
}: ProposalSectionProps) => {
  const { buildRules } = section;

  if (buildRules !== undefined && !runBuildRules(buildRules, proposal, user)) {
    return null;
  }

  let isDisabled = divisionDisabled;
  let sidebarTooltip;

  if (!isDisabled) {
    const validation =
      section.disableRules == null
        ? { isDisabled: false, sidebarTooltip: undefined }
        : validateDisableRules(section.disableRules, selectedSections, proposal.items, proposal.riskProfile);

    isDisabled = validation.isDisabled;
    sidebarTooltip = validation.sidebarTooltip;
  }

  const toggleSection = (section: ProposalNS.ProposalTemplateDivisionElement | ProposalNS.ProposalTemplateSectionElement, selected: boolean) => {
    toggleLoadingSection(section.key, !sectionIsSelected(section.key));

    // Unblock the UI thread to allow the loading spinner to render
    // In React18 this would be handled properly with useTransition
    setTimeout(() => {
      onToggleSection(section.key, selected);
    }, 0);
  };

  const toggleLoadingSection = (sectionKey: string, loading: boolean) => {
    selectedSectionsLocalState.set(sectionKey, loading);
    setSelectedSectionsLocalState(new Map(selectedSectionsLocalState));
  };

  const sectionIsSelected = (sectionKey: string): boolean => selectedSections.indexOf(sectionKey) > -1;

  const sectionIsLoading = (sectionKey: string): boolean =>
    selectedSectionsLocalState.has(sectionKey) && sectionIsSelected(sectionKey) !== selectedSectionsLocalState.get(sectionKey);

  switch (section.sidebarControl) {
    case ProposalNS.ProposalSidebarControl.Toggle: {
      if (filterQuery.length > 2 && !showWholeDivision && !results.includes(section.label)) {
        return null;
      }

      return (
        <div className={classNames('proposal-checklist-option')} key={`section ${section.key}`}>
          <ToggleSwitch
            checked={!isDisabled && sectionIsSelected(section.key)}
            onChange={checked => toggleSection(section, checked)}
            disabled={isDisabled}
            inverted
          >
            <>
              <span className="label">
                {sectionIsLoading(section.key) && showLoaders && <LoadingSpinner />}
                {formatLocalizedString(section.label)}
              </span>

              {section.sidebarTooltip && (
                <InfoTooltip
                  title={formatLocalizedString(section.sidebarTooltip.title)}
                  message={formatLocalizedString(section.sidebarTooltip.message)}
                  overridePosition={overrideTooltipPosition}
                />
              )}
            </>
          </ToggleSwitch>
          <HideableLayout
            section={section}
            isDisabled={isDisabled}
            selectedSections={selectedSections}
            proposal={proposal}
            overrideTooltipPosition={overrideTooltipPosition}
            onToggleHiddenLayout={onToggleHiddenLayout}
            hiddenLayouts={hiddenLayouts}
          />
        </div>
      );
    }

    case ProposalNS.ProposalSidebarControl.Radio:
      if (filterQuery.length > 2 && !showWholeDivision && !results.includes(section.label)) {
        return null;
      }

      return (
        <div className={classNames('proposal-checklist-option')} key={`section ${section.key}`}>
          <Radio
            selected={sectionIsSelected(section.key)}
            onChange={checked => toggleSection(section, checked)}
            label={
              <>
                {sectionIsLoading(section.key) && showLoaders && <LoadingSpinner />}
                {formatLocalizedString(section.label)}
              </>
            }
            disabled={isDisabled}
          />
          <HideableLayout
            section={section}
            isDisabled={isDisabled}
            selectedSections={selectedSections}
            proposal={proposal}
            overrideTooltipPosition={overrideTooltipPosition}
            onToggleHiddenLayout={onToggleHiddenLayout}
            hiddenLayouts={hiddenLayouts}
          />
        </div>
      );

    case ProposalNS.ProposalSidebarControl.Checkbox:
      if (filterQuery.length > 2 && !showWholeDivision && !results.includes(section.label)) {
        return null;
      }

      let output;
      const checkbox = (
        <div>
          <Checkbox
            selected={!isDisabled && sectionIsSelected(section.key)}
            onChange={checked => toggleSection(section, checked)}
            label={
              <>
                {sectionIsLoading(section.key) && showLoaders && <LoadingSpinner />}
                {formatLocalizedString(section.label)}
              </>
            }
            disabled={isDisabled}
          />
          <HideableLayout
            section={section}
            isDisabled={isDisabled}
            selectedSections={selectedSections}
            proposal={proposal}
            overrideTooltipPosition={overrideTooltipPosition}
            onToggleHiddenLayout={onToggleHiddenLayout}
            hiddenLayouts={hiddenLayouts}
          />
        </div>
      );

      if (isDisabled && sidebarTooltip) {
        output = (
          <div className="d-flex align-items-center">
            {checkbox}
            <InfoTooltip
              place="right"
              title={formatLocalizedString(sidebarTooltip.title)}
              message={formatLocalizedString(sidebarTooltip.message)}
              overridePosition={overrideTooltipPosition}
            />
          </div>
        );
      } else {
        output = checkbox;
      }

      return (
        <div key={`section ${section.key}`} className={classNames('proposal-checklist-option', { disabled: isDisabled })}>
          {output}
        </div>
      );

    case ProposalNS.ProposalSidebarControl.None:
      return (
        <div className={classNames('proposal-checklist-option')} key={`section ${section.key}`}>
          <span className="label">{formatLocalizedString(section.label)}</span>
        </div>
      );
  }

  return null;
};

export default ProposalSection;
